"use client"

import { useCallback, useEffect, useMemo } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useRouter, usePathname } from "next/navigation"
import errorToast from "../../../shared/lib/toast/error.toast"
import successToast from "../../../shared/lib/toast/success.toast"
import WorkOrderTable from "./work-orders-table"
import WorkOrdersResultsCount from "./work-orders-results-count"
import WorkOrdersFilterIndicator from "./work-orders-filter-indicator"
import {
    PaginationData,
    usePaginationData,
} from "../context/pagination-data-context"
import { useWorkOrdersList } from "../hooks/use-work-orders.hook"
import {
    updateWorkOrderStatus,
    getAllWorkOrders,
    getWorkScopeFilterOptions,
} from "@/features/work-orders/api/work-orders.api"
import { useSession } from "../../auth/context/session.context"
import { UserRole } from "../../../shared/constants/user-roles"
import { WorkOrder } from "../types/work-order.type"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { buildUrl } from "@/shared/lib/utils"
import { WORK_ORDERS } from "@/shared/constants/backend-api"

/**
 * WorkOrdersList component
 * Responsible for fetching, managing pagination, and displaying work orders data
 */
function WorkOrdersList() {
    // Get pagination context setter
    const { setPaginationData } = usePaginationData()
    const queryClient = useQueryClient()
    const router = useRouter()
    const pathname = usePathname()
    const { regionId } = useRegionId()

    // Get session for role-based access control
    const { session, profile } = useSession()
    const canManageWorkOrders =
        session?.role === UserRole.ADMIN ||
        session?.role === UserRole.BASE_LEADER

    // Privacy: hide filters for Volunteer, Homeowner, Supporter (search only)
    const hideFilters = [
        UserRole.VOLUNTEER,
        UserRole.HOMEOWNER,
        UserRole.Supporter,
    ].includes(session?.role as UserRole)

    const { data, isLoading, queryParams } = useWorkOrdersList()

    // Fetch option lists for column filter comboboxes (skip when filters hidden)
    const { data: optionsData } = useQuery({
        queryKey: [WORK_ORDERS.getAll, "options", regionId],
        queryFn: () =>
            getAllWorkOrders({
                regionId: regionId!,
                limit: 1000,
            }),
        enabled: !!regionId && !hideFilters,
    })

    const { data: workScopeOptionsData } = useQuery({
        queryKey: [WORK_ORDERS.workScopeOptions, regionId],
        queryFn: () => getWorkScopeFilterOptions(regionId!),
        enabled: !!regionId && !hideFilters,
    })

    const workScopeOptions = useMemo(() => {
        if (!workScopeOptionsData?.success || !Array.isArray(workScopeOptionsData.data)) {
            return [] as { value: string; label: string }[]
        }
        return workScopeOptionsData.data
    }, [workScopeOptionsData])

    const filterOptions = useMemo(() => {
        if (!optionsData?.success || !optionsData?.data?.workOrders) {
            return {
                homeownerNames: [] as string[],
                ownerOptions: [] as { id: number; fullname: string }[],
                teamLeadNames: [] as string[],
                teamNames: [] as string[],
            }
        }
        const list = optionsData.data.workOrders as any[]
        const homeownerNames = Array.from(
            new Set(
                list
                    .map((wo) => wo.home?.homeowner?.user?.fullname?.trim())
                    .filter(Boolean)
            )
        ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        const ownerMap = new Map<number, string>()
        list.forEach((wo) => {
            if (wo.owner?.id && wo.owner?.fullname)
                ownerMap.set(wo.owner.id, wo.owner.fullname)
        })
        const ownerOptions = Array.from(ownerMap.entries())
            .map(([id, fullname]) => ({ id, fullname }))
            .sort((a, b) => a.fullname.localeCompare(b.fullname))
        const teamLeadSet = new Set(list.map((wo) => wo.teamLead?.fullname?.trim()).filter(Boolean))
        teamLeadSet.add("Not assigned")
        const teamLeadNames = Array.from(teamLeadSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        const teamNameSet = new Set(list.map((wo) => wo.teamName?.trim()).filter(Boolean))
        teamNameSet.add("Not assigned")
        const teamNames = Array.from(teamNameSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        return { homeownerNames, ownerOptions, teamLeadNames, teamNames }
    }, [optionsData])

    const { page, limit } = queryParams
    const hasActiveFilters = useMemo(() => {
        const ignoredKeys = new Set(["page", "limit", "sortBy", "sortOrder"])
        return Object.entries(queryParams as Record<string, unknown>).some(
            ([key, value]) =>
                !ignoredKeys.has(key) &&
                value !== undefined &&
                value !== null &&
                value !== "" &&
                !(Array.isArray(value) && value.length === 0)
        )
    }, [queryParams])

    // Get work orders directly from API response (no client-side filtering)
    const workOrders = useMemo(() => {
        if (!data?.success || !data.data?.workOrders) {
            return []
        }
        return data.data.workOrders
    }, [data?.data?.workOrders, data?.success])

    // Get total count from API response
    const total = useMemo(() => {
        if (!data?.success || !data.data) {
            return 0
        }
        return data.data.total
    }, [data?.data, data?.success])

    const updatedPaginatedData = useCallback(
        (data: PaginationData) => {
            setPaginationData({
                page: data.page,
                limit: data.limit,
                total: data.total,
            })
        },
        [setPaginationData]
    )

    // Update pagination context when data changes
    useEffect(() => {
        if (data?.success && data.data) {
            updatedPaginatedData({
                page: data.data.page,
                limit: data.data.limit,
                total: total,
            })
        }
    }, [data, limit, page, total, updatedPaginatedData])

    // Handle work order status change
    const handleStatusChange = useCallback(
        async (workOrderId: number, newStatus: string) => {
            try {
                const result = await updateWorkOrderStatus(
                    workOrderId,
                    newStatus
                )
                if (result.success) {
                    successToast(`Work order status updated to ${newStatus === "Active" ? "Open" : "Closed"}`)
                    queryClient.invalidateQueries({ queryKey: [WORK_ORDERS.getAll] })
                } else {
                    errorToast(
                        result.message || "Failed to update work order status"
                    )
                }
            } catch (error) {
                errorToast("Failed to update work order status")
                console.error("Error updating work order status:", error)
            }
        },
        [queryClient]
    )

    const pushParams = useCallback(
        (next: Record<string, unknown>) => {
            const q = { ...next, page: 1 }
            router.push(buildUrl(pathname, q as Record<string, string | number | string[] | undefined | null>))
        },
        [pathname, router]
    )

    const onHomeownerNameFilter = useCallback(
        (v: string | null) => {
            const next = { ...queryParams }
            if (v) (next as any).homeownerName = v
            else delete (next as any).homeownerName
            pushParams(next)
        },
        [queryParams, pushParams]
    )

    const onAddressFilter = useCallback(
        (opts: { city: string | null; street: string | null }) => {
            const next = { ...queryParams } as any
            if (opts.city) next.city = opts.city
            else delete next.city
            if (opts.street) next.street = opts.street
            else delete next.street
            pushParams(next)
        },
        [queryParams, pushParams]
    )

    const onWorkScopeFilter = useCallback(
        (v: string[] | null) => {
            const next = { ...queryParams } as any
            if (v && v.length) next.workOrderType = v
            else delete next.workOrderType
            pushParams(next)
        },
        [queryParams, pushParams]
    )

    const onOwnerFilter = useCallback(
        (v: string | null) => {
            const next = { ...queryParams } as any
            if (v) next.ownerId = v
            else delete next.ownerId
            pushParams(next)
        },
        [queryParams, pushParams]
    )

    const onTeamLeadFilter = useCallback(
        (v: string | null) => {
            const next = { ...queryParams } as any
            if (v) next.delegateTo = v
            else delete next.delegateTo
            pushParams(next)
        },
        [queryParams, pushParams]
    )

    const onTeamNameFilter = useCallback(
        (v: string | null) => {
            const next = { ...queryParams } as any
            if (v) next.teamName = v
            else delete next.teamName
            pushParams(next)
        },
        [queryParams, pushParams]
    )

    const onCreatedDateFilter = useCallback(
        (v: string | null) => {
            const next = { ...queryParams } as any
            if (v) next.createdDate = v
            else delete next.createdDate
            pushParams(next)
        },
        [queryParams, pushParams]
    )

    const onStatusFilter = useCallback(
        (v: string | null) => {
            const next = { ...queryParams } as any
            if (v) next.status = v
            else delete next.status
            pushParams(next)
        },
        [queryParams, pushParams]
    )

    // Handle error states
    if (data && !data.success) {
        errorToast(
            data?.message ||
                "Something went wrong while fetching work orders"
        )
        return null
    }

    // Handle loading state
    if (isLoading && !data) {
        return (
            <div className="space-y-4">
                <WorkOrdersResultsCount total={0} isLoading={true} />
                <WorkOrderTable
                    companyList={[]}
                    isLoading={true}
                    canManageWorkOrders={false}
                    hasActiveFilters={hasActiveFilters}
                />
            </div>
        )
    }

    const homeownerName = (queryParams as any).homeownerName ?? ""
    const city = (queryParams as any).city ?? ""
    const street = (queryParams as any).street ?? ""
    const w = (queryParams as any).workOrderType
    const workScope = Array.isArray(w) ? w.slice() : (typeof w === "string" && w ? [w] : [])
    const ownerId = (queryParams as any).ownerId ?? ""
    const teamLead = (queryParams as any).delegateTo ?? ""
    const teamNameVal = (queryParams as any).teamName
    const teamNameFilter = typeof teamNameVal === "string" ? teamNameVal : Array.isArray(teamNameVal) ? teamNameVal[0] ?? "" : ""
    const createdDate = (queryParams as any).createdDate ?? ""
    const statusFilter = (queryParams as any).status ?? null

    // Render the work orders table component
    return (
        <div className="space-y-4">
            {!hideFilters && <WorkOrdersFilterIndicator />}
            <WorkOrdersResultsCount
                total={total}
                isLoading={isLoading}
            />
            <WorkOrderTable
                companyList={workOrders}
                isLoading={isLoading}
                onStatusChange={handleStatusChange}
                canManageWorkOrders={canManageWorkOrders}
                userRole={session?.role || null}
                userId={profile?.id || null}
                sessionSub={session?.sub ?? null}
                hasActiveFilters={hasActiveFilters}
                {...(hideFilters
                    ? {}
                    : {
                        homeownerNameFilter: homeownerName,
                        onHomeownerNameFilter: onHomeownerNameFilter,
                        homeownerNames: filterOptions.homeownerNames,
                        cityFilter: city,
                        streetFilter: street,
                        onAddressFilter: onAddressFilter,
                        workScopeFilter: workScope,
                        onWorkScopeFilter: onWorkScopeFilter,
                        workScopeOptions,
                        ownerIdFilter: ownerId,
                        onOwnerFilter: onOwnerFilter,
                        ownerOptions: filterOptions.ownerOptions,
                        teamLeadFilter: teamLead,
                        onTeamLeadFilter: onTeamLeadFilter,
                        teamLeadNames: filterOptions.teamLeadNames,
                        teamNameFilter,
                        onTeamNameFilter: onTeamNameFilter,
                        teamNames: filterOptions.teamNames,
                        createdDateFilter: createdDate,
                        onCreatedDateFilter: onCreatedDateFilter,
                        statusFilter,
                        onStatusFilter: onStatusFilter,
                    })}
            />
        </div>
    )
}

export default WorkOrdersList
